import { useCallback, useEffect, useRef } from 'react';
import { PricingTable } from '@clerk/clerk-react';
import { clerkPricingTableAppearance } from '../lib/clerk-appearance';
import { useForceMonthlyDefault } from '@/lib/useForceMonthlyDefault';
import { useBillingInfo } from '../hooks/useBillingInfo';
import { useBillingPageExit } from '../hooks/useBillingPageExit';
import { TrialExpiredPlanGrid } from '../components/billing/TrialExpiredPlanGrid';
import { BillingPlanCard, PLAN_NAME_ID } from '../components/billing/BillingPlanCard';
import { BillingSkeleton } from '../components/billing/BillingSkeleton';
import { CheckoutReturnRefresher } from '../components/billing/StripeBilling';
import { ActivationPending, SubscriptionSuccess } from '../components/billing/SubscriptionActivation';
import { useSubscriptionActivation } from '../hooks/useSubscriptionActivation';
import { capture } from '../lib/analytics';
import { LoadError } from '../components/shared/IssuesLoadError';
import type { TeamBillingInfo } from '@overlayqa/shared';

// Shared prop shape for the two billing view events, so `billing_page_viewed`
// (warm mounts) and `billing_page_data_loaded` cannot drift apart.
function buildBillingViewProps(billingInfo: TeamBillingInfo): Record<string, unknown> {
  return {
    current_plan: billingInfo.planName,
    plan_id: billingInfo.plan,
    is_owner: billingInfo.isOwner,
    seats_used: billingInfo.currentSeats,
    max_seats: billingInfo.maxSeats === Infinity ? null : billingInfo.maxSeats,
    in_beta: Boolean(
      billingInfo.betaExpiresAt && new Date(billingInfo.betaExpiresAt) > new Date()
    ),
  };
}

export function BillingPage() {
  const pricingRef = useRef<HTMLDivElement | null>(null);
  const forceMonthlyRef = useForceMonthlyDefault();
  // The pricing container mounts only after billing info loads, so the
  // monthly-default hook takes a callback ref (fires on actual mount);
  // pricingRef stays for the delegated analytics click listener below.
  const setPricingNode = useCallback(
    (node: HTMLDivElement | null) => {
      pricingRef.current = node;
      forceMonthlyRef(node);
    },
    [forceMonthlyRef]
  );

  const { data: billingInfo, isLoading, error, refetch, teamId } = useBillingInfo();
  const { activation, start: startActivation, dismissSuccess } = useSubscriptionActivation();
  const confirmingPlan = activation.status === 'pending';

  // Fire `billing_page_viewed` exactly once at MOUNT — never gated on data.
  // The data-gated version structurally missed short visits: a 14.4s
  // pre-churn billing visit on 2026-09-01 emitted nothing (rrweb-proven,
  // deep-sweep analytics entry), which is exactly the population a pricing
  // read needs. Plan props ride along only when billingInfo is already cached
  // at mount (`data_loaded_at_view: true`); otherwise the separate
  // `billing_page_data_loaded` marker below carries them, so a view with no
  // marker IS the short-visit/loading-state population. One-shot refs latch
  // deliberately so a StrictMode double-mount cannot double-count a view.
  const viewedRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    capture('billing_page_viewed', {
      data_loaded_at_view: Boolean(billingInfo),
      ...(billingInfo ? buildBillingViewProps(billingInfo) : {}),
    });
    // Mount-time snapshot on purpose: the data-arrival case is the marker's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!billingInfo || dataLoadedRef.current) return;
    dataLoadedRef.current = true;
    capture('billing_page_data_loaded', {
      ...buildBillingViewProps(billingInfo),
      ms_since_view: Date.now() - mountedAtRef.current,
    });
  }, [billingInfo]);

  /**
   * Which chooser is on screen. Hoisted out of the JSX below so
   * `billing_page_exited` can name it: the two surfaces offer different CTAs
   * to different people, and pooling them would make a Clerk-table visit and a
   * trial-grid visit indistinguishable in the one event that reports what the
   * reader saw. `none` also covers members, who cannot manage this team's plan.
   */
  const showTrialGrid = Boolean(
    billingInfo &&
      billingInfo.isOwner &&
      (billingInfo.plan === null ||
        (billingInfo.betaExpiresAt && new Date(billingInfo.betaExpiresAt) > new Date()))
  );
  // A team paying through Stripe manages its plan on Stripe's customer portal.
  // Keyed on where the plan CAME FROM, not on the release flag: a Stripe payer
  // must keep this surface if ENABLE_STRIPE_CHECKOUT is later turned off, and
  // must never be shown Clerk's PricingTable (Clerk has no record of their
  // subscription and would sell them the plan they hold, via a second biller).
  const isStripePayer = billingInfo?.planSource === 'stripe';
  const surface = !billingInfo?.isOwner
    ? 'none'
    : showTrialGrid
      ? 'trial_grid'
      : isStripePayer
        ? 'stripe_manage'
        : 'clerk_pricing_table';

  // Resolved at EXIT time, not at mount: on a cold load the plan props are not
  // known yet when the hook arms, and a visit that ends before the fetch lands
  // is exactly the short-visit population this event exists to see.
  const resolveExitProps = useCallback(
    () => ({
      surface,
      ...(billingInfo ? buildBillingViewProps(billingInfo) : {}),
    }),
    [surface, billingInfo]
  );
  const { chooserRef, markInteraction } = useBillingPageExit({ resolveProps: resolveExitProps });

  // Clerk's PricingTable renders its own DOM; we can't wrap each plan button.
  // Use a delegated click handler on the container to capture upgrade intent.
  // The button text gives us the plan name; we treat any click as
  // `billing_pricing_table_click` and let the server webhook fire the
  // authoritative `plan_upgraded` event from Clerk's subscription.created.
  useEffect(() => {
    const node = pricingRef.current;
    if (!node) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const button = target.closest('button, a');
      if (!button) return;
      const label = (button.textContent || '').trim().slice(0, 80);
      capture('billing_pricing_table_click', {
        label,
        current_plan: billingInfo?.planName,
      });
      markInteraction('cta');
    };
    node.addEventListener('click', onClick);
    return () => node.removeEventListener('click', onClick);
  }, [billingInfo?.planName, markInteraction]);

  return (
    <section id="billing" className="page-content">
      {/* mx-auto: Billing centres its column (Emily, 2026-09-17), an
          exception to the app-wide left alignment in .page-content__inner
          (Reports is the other). */}
      <div className="page-content__inner page-content__inner--app mx-auto">

      {isLoading && (
        <BillingSkeleton />
      )}

      {error && (
        <LoadError resource="billing information" error={error} onRetry={() => refetch()} recoveryFocusId={PLAN_NAME_ID} />
      )}

      {billingInfo && (
        <>
          {/* Back from Stripe Checkout: the return lands before the webhook has
              necessarily flipped the plan, so the activation flow polls and says
              so. Mounted for every state, because on return the page may still
              read "trial". */}
          <CheckoutReturnRefresher onSuccessReturn={() => void startActivation('stripe_return')} />

          {confirmingPlan && <ActivationPending />}
          {activation.status === 'active' && activation.successOpen && (
            <SubscriptionSuccess
              planName={activation.info.planName}
              seats={activation.info.maxSeats}
              returnFocusId={PLAN_NAME_ID}
              onDismiss={dismissSuccess}
            />
          )}

          {/* Hidden, not unmounted, while the plan is confirmed: no second
              purchase is reachable, and Clerk's checkout drawer (owned by the
              plan grid) is not torn down under the buyer. */}
          <div hidden={confirmingPlan}>
          <BillingPlanCard billingInfo={billingInfo} onUpgrade={() => markInteraction('cta')} justActivated={activation.status === 'active'} />

          {/*
           * Plan chooser. Two surfaces:
           *
           * 1. TrialExpiredPlanGrid — for any owner without an active plan,
           *    AND for owners whose plan comes from an ACTIVE beta.
           *    Clerk's PricingTable would show "Start free trial"
           *    buttons because Clerk has no record of OverlayQA's beta
           *    trial history. We render our own grid with explicit
           *    "Subscribe" CTAs so neither group can stack a Clerk free
           *    trial on top of whatever access they already had — plan-less
           *    owners regardless of whether `teams.beta_expires_at` was
           *    ever set (legacy users from before auto-grant landed all
           *    have `beta_expires_at = null`), and beta-active owners whose
           *    Team access is invisible to Clerk (2026-07-03 billing QA:
           *    the table offered "Start 7-day free trial" to a beta user
           *    whose Current Plan card said Team/Active).
           *    `betaExpiresAt` is only populated while the team's access
           *    COMES FROM the beta trial: the server withholds it the moment
           *    an active Clerk subscription governs the team (billing.service
           *    getTeamBillingInfo, gated on plan provenance), so paid
           *    subscribers always fall through to the PricingTable. The row
           *    keeps its date for `hadTrial`; nothing nulls the column on pay.
           *
           * 2. Clerk's PricingTable — for owners with an active PAID plan
           *    who want to upgrade / downgrade. The table purchases a personal
           *    subscription; it does not enforce our team ownership. Only the
           *    owner's subscription grants team access (billing.service.ts:166).
           *    isOwner compares the requester to teams.ownerId (same file:382),
           *    so neither chooser belongs on a member's page.
           */}
          {billingInfo.isOwner && (
            <div id="billing-plans" ref={chooserRef} tabIndex={-1}>
              {showTrialGrid ? (
                <TrialExpiredPlanGrid
                  currentPlanSlug={billingInfo.plan}
                  onInteraction={markInteraction}
                  checkoutProvider={billingInfo.checkoutProvider}
                  checkoutSeatsAvailable={billingInfo.checkoutSeatsAvailable}
                  currentSeats={billingInfo.currentSeats}
                  selectionKey={teamId}
                  // `betaExpiresAt` is only ever populated while a trial is ACTIVE
                  // and the server withholds it once a paid Clerk subscription
                  // governs the team (TeamBillingInfo docs), so its presence is the
                  // trial/paid discriminator the grid needs to tell the trial
                  // plan's "Subscribe to Team" + "Your trial plan" badge from
                  // the paid "Your current plan".
                  isTrialing={
                    !!billingInfo.betaExpiresAt && new Date(billingInfo.betaExpiresAt) > new Date()
                  }
                  onSubscriptionComplete={() => void startActivation('clerk_drawer')}
                />
              ) : isStripePayer ? null : (
                <div ref={setPricingNode}>
                  <PricingTable appearance={clerkPricingTableAppearance} />
                </div>
              )}
            </div>
          )}
          </div>
        </>
      )}
      </div>
    </section>
  );
}
